import { SeatsRangeEnum, VehicleClass, zo } from 'remote/global';
import { PricingAdjustmentTypeEnum } from 'utils/data/enums';
import { DaysOfWeek } from 'utils/data/data-types';
import dayjs from 'dayjs';
import {
  transformFloatDecimalSeparator,
  transformToDoubleDecimals,
  transformToSingleDecimal,
  transformToTripleDecimals,
} from '../helpers';

const carrierCostSchema = zo
  .object({
    seatsFrom: zo.number().optional(),
    seatsTo: zo.number().optional(),
    fuelLitersPerDistanceUnit: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gt(0).lte(1).transform(transformToDoubleDecimals)),
    driverHourlyRate: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gt(0).lte(100).transform(transformToDoubleDecimals)),
    vehicleCostsPerDistanceUnit: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gte(0).lte(100).transform(transformToDoubleDecimals)),
    vehicleCostsPerHour: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gte(0).lte(100).transform(transformToDoubleDecimals)),
    adminCostsPerHour: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gte(0).lte(100).transform(transformToDoubleDecimals)),
    departureCost: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gte(0).lte(500).transform(transformToDoubleDecimals)),
    dailyMargin: zo.coerce
      .string()
      .min(1)
      .transform(transformFloatDecimalSeparator)
      .pipe(zo.coerce.number().gte(0).lte(1000).transform(transformToDoubleDecimals)),
  })
  .superRefine((data, ctx) => {
    // One of vehicleCostsPerDistanceUnit or vehicleCostsPerHour has to have a value
    const isOneOrOther =
      Boolean(data.vehicleCostsPerDistanceUnit) || Boolean(data.vehicleCostsPerHour);

    if (!isOneOrOther)
      ['vehicleCostsPerDistanceUnit', 'vehicleCostsPerHour'].map((path) =>
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: { i18n: 'oneOrOther' },
          path: [path],
        })
      );
  });

const commonSchemaObject = zo.object({
  dailyAllowance: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(500)),
  fuelPriceLiterExclVat: zo.coerce
    .string()
    .min(1)
    .transform(transformFloatDecimalSeparator)
    .pipe(zo.coerce.number().gt(0).lte(5).transform(transformToTripleDecimals)),
  basicRatePctSmall: zo.coerce.string().min(1).pipe(zo.coerce.number().int().lte(0).gte(-100)),
  basicRatePctLarge: zo.coerce.string().min(1).pipe(zo.coerce.number().int().lte(0).gte(-100)),
  executiveRatePctSmall: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(100)),
  executiveRatePctLarge: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(100)),
});

const carrierPricingCostsSchema = zo.object({
  bySeats: zo.array(carrierCostSchema),
  common: commonSchemaObject,
});

const pricingRulesDtoSchema = carrierPricingCostsSchema.extend({
  operatorId: zo.number(),
});

const pricingCalculatorSchema = zo.object({
  common: commonSchemaObject,
  days: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(15)),
  hours: zo.coerce
    .string()
    .min(1)
    .transform(transformFloatDecimalSeparator)
    .pipe(zo.coerce.number().gte(0).lt(24).transform(transformToSingleDecimal)),
  mileage: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gt(0).lte(10_000)),
  numberOfDrivers: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gt(0).lte(2)),
  otherCostsExclVat: zo.coerce
    .string()
    .min(1)
    .transform(transformFloatDecimalSeparator)
    .pipe(zo.coerce.number().gte(0).lte(1000).transform(transformToDoubleDecimals)),
  tollRoadsCostsExclVat: zo.coerce
    .string()
    .min(1)
    .transform(transformFloatDecimalSeparator)
    .pipe(zo.coerce.number().gte(0).lte(1000).transform(transformToDoubleDecimals)),
});

const calculatorResponseDtoSchema = zo.object({
  adjustmentPct: zo.number(),
  adjustmentType: zo.nativeEnum(PricingAdjustmentTypeEnum),
  costs: zo.object({
    adminCost: zo.number(),
    departureCost: zo.number(),
    driversCost: zo.number(),
    fuelCost: zo.number(),
    vehicleMileageCost: zo.number(),
    vehicleTimeCost: zo.number(),
  }),
  days: zo.number(),
  finalPriceExclVat: zo.number(),
  finalPriceInclVat: zo.number(),
  hours: zo.number(),
  mileageKm: zo.number(),
  netProfitMargin: zo.number(),
  seatsFrom: zo.number(),
  seatsTo: zo.number(),
  sumOfTotalCosts: zo.number(),
  unadjustedPriceExclVat: zo.number(),
  unadjustedPriceInclVat: zo.number(),
  vehicleClass: zo.nativeEnum(VehicleClass),
  vehicleClassAdjustmentPct: zo.number(),
});

const pricingPeriodSchema = zo
  .object({
    // Can be an auto-gen uuid
    id: zo.number().or(zo.string()).optional(),
    type: zo.coerce.string().min(1).pipe(zo.nativeEnum(PricingAdjustmentTypeEnum)),
    vehicleClass: zo.nativeEnum(VehicleClass).or(zo.literal('')).optional(),
    seatsRange: zo.nativeEnum(SeatsRangeEnum).or(zo.literal('')).optional(),
    validFrom: zo
      .string()
      .min(1)
      .transform((value) => dayjs(value).format('YYYY-MM-DD')),
    validTo: zo
      .string()
      .min(1)
      .transform((value) => dayjs(value).format('YYYY-MM-DD')),
    startTime: zo.string().min(1),
    endTime: zo.string().min(1),
    // TODO: Lisa võimalus "Kõik" & Tööpäevad
    daysOfWeek: zo.array(zo.nativeEnum(DaysOfWeek)).min(1),
    adjustmentPct: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(300)),
  })
  .superRefine((data, ctx) => {
    const { validFrom, validTo } = data;

    const isToBeforeFrom = dayjs(validTo) < dayjs(validFrom);
    if (isToBeforeFrom) {
      ['validFrom', 'validTo'].map((path, idx) =>
        ctx.addIssue({
          code: zo.ZodIssueCode.custom,
          params: {
            i18n: {
              key: idx === 0 ? 'dateAfterAnother_with_value' : 'dateBeforeAnother_with_value',
              values: { other: idx === 0 ? 'paths.validTo' : 'paths.validFrom' },
            },
          },
          path: [path],
        })
      );
    }
  });

const pricingPeriodsSchema = zo.object({
  rows: zo.array(pricingPeriodSchema),
});

const pricingResponseSchema = zo.object({
  adjustmentPct: zo.number(),
  adjustmentType: zo.nativeEnum(PricingAdjustmentTypeEnum),
  costs: carrierPricingCostsSchema,
  days: zo.number(),
  finalPriceExclVat: zo.number(),
  finalPriceInclVat: zo.number(),
  hours: zo.number(),
  mileageKm: zo.number(),
  netProfitMargin: zo.number(),
  seatsFrom: zo.number(),
  seatsTo: zo.number(),
  sumOfTotalCosts: zo.number(),
  supplierContractDiscountPct: zo.number().optional(),
  unadjustedPriceExclVat: zo.number(),
  unadjustedPriceInclVat: zo.number(),
  vehicleClass: zo.nativeEnum(VehicleClass),
  vehicleClassAdjustmentPct: zo.number().optional(),
});

type CarrierPricingCostsSchemaType = zo.infer<typeof carrierPricingCostsSchema>;
type PricingCalculatorSchemaType = zo.infer<typeof pricingCalculatorSchema>;
type PricingPeriodSchemaType = zo.infer<typeof pricingPeriodSchema>;
type PricingPeriodsSchemaType = zo.infer<typeof pricingPeriodsSchema>;
type CalculatorResponseDtoSchemaType = zo.infer<typeof calculatorResponseDtoSchema>;
type PricingRulesDtoSchemaType = zo.infer<typeof pricingRulesDtoSchema>;
type PricingResponseSchemaType = zo.infer<typeof pricingResponseSchema>;

export {
  carrierPricingCostsSchema,
  pricingCalculatorSchema,
  pricingPeriodSchema,
  pricingPeriodsSchema,
};
export type {
  CarrierPricingCostsSchemaType,
  PricingCalculatorSchemaType,
  PricingPeriodSchemaType,
  PricingPeriodsSchemaType,
  CalculatorResponseDtoSchemaType,
  PricingRulesDtoSchemaType,
  PricingResponseSchemaType,
};
